import React, { Component } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import Radio from "@mui/material/Radio";
import MicIcon from "@mui/icons-material/Mic";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import VideocamIcon from "@mui/icons-material/Videocam";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import { toast } from "react-toastify";
import { playNotificationSound } from "../../config/notification-handler";

const SECURE_CONTEXT_OK = () =>
  window.isSecureContext ||
  window.location.protocol === "https:" ||
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const STORAGE_KEYS = {
  microphone: "selectedConsultMicInput",
  speaker: "selectedConsultCallingDevice",
  camera: "selectedConsultCamera",
};

class ConsultDeviceDock extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      openKind: null, // 'microphone' | 'speaker' | 'camera'
      loadingDevices: false,
      audioInputs: [],
      audioOutputs: [],
      videoInputs: [],
      selectedMicInput: localStorage.getItem(STORAGE_KEYS.microphone) || "",
      selectedCallingDevice:
        localStorage.getItem(STORAGE_KEYS.speaker) || "",
      selectedCamera: localStorage.getItem(STORAGE_KEYS.camera) || "",

      // Microphone test recording state
      isRecording: false,
      mediaRecorder: null,
      audioStream: null,
      recordedAudioBlob: null,
      recordedAudioUrl: null,
      isPlayingRecording: false,
      playbackAudio: null,

      // Headset/speaker test state
      showHeadsetTestButtonIcon: true, // true=play icon, false=pause icon
    };

    // Used to prevent async `MediaRecorder.onstop` from updating UI
    // when recording is cancelled/closed/unmounted.
    this._ignoreNextRecordingResult = false;
    this._isUnmounted = false;
  }

  getVariant = () => {
    return this.props.variant || "pills"; // 'pills' | 'icons'
  };

  componentDidMount() {
    this._deviceChangeHandler = () => {
      // Only reload while mounted/open; cheap enough and avoids stale UI.
      this.loadDevices();
    };
    if (
      navigator.mediaDevices &&
      navigator.mediaDevices.addEventListener &&
      navigator.mediaDevices.enumerateDevices
    ) {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        this._deviceChangeHandler
      );
    }

    this.loadDevices();
  }

  componentWillUnmount() {
    this._isUnmounted = true;
    this._ignoreNextRecordingResult = true;

    if (
      navigator.mediaDevices &&
      navigator.mediaDevices.removeEventListener &&
      this._deviceChangeHandler
    ) {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        this._deviceChangeHandler
      );
    }

    this.stopMicRecording();
    this.stopMicPlayback();

    if (this.state.recordedAudioUrl) {
      try {
        URL.revokeObjectURL(this.state.recordedAudioUrl);
      } catch (e) {
        // ignore
      }
    }
  }

  loadDevices = async () => {
    if (!SECURE_CONTEXT_OK()) {
      this.setState({
        audioInputs: [],
        audioOutputs: [],
        videoInputs: [],
      });
      return;
    }

    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia ||
      !navigator.mediaDevices.enumerateDevices
    ) {
      this.setState({
        audioInputs: [],
        audioOutputs: [],
        videoInputs: [],
      });
      return;
    }

    this.setState({ loadingDevices: true });
    try {
      // Request permission so device labels are populated.
      const constraints = this.props.isAudioOnlyConsult
        ? { audio: true }
        : { audio: true, video: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach((t) => t.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioOutputs = devices.filter(
        (d) =>
          d.kind === "audiooutput" &&
          d.deviceId !== "communications" &&
          d.deviceId !== "default"
      );
      // Hide "Test Speakers" device entries (not part of production UI).
      const audioOutputsFiltered = audioOutputs.filter((d) => {
        const label = (d.label || "").toLowerCase();
        return !/test\s*speakers?/.test(label);
      });
      const audioInputs = devices.filter((d) => d.kind === "audioinput");
      const videoInputs = devices.filter((d) => d.kind === "videoinput");

      this.setState({
        audioInputs,
        audioOutputs: audioOutputsFiltered,
        videoInputs,
        selectedMicInput:
          localStorage.getItem(STORAGE_KEYS.microphone) || "",
        selectedCallingDevice:
          localStorage.getItem(STORAGE_KEYS.speaker) || "",
        selectedCamera: localStorage.getItem(STORAGE_KEYS.camera) || "",
      });
    } catch (err) {
      // Labels might still be unavailable, but keep UI functional.
      console.warn("ConsultDeviceDock: loadDevices failed", err);
    } finally {
      this.setState({ loadingDevices: false });
    }
  };

  getLabelForDeviceId = (deviceId, devices) => {
    if (!deviceId) return "Default";
    const device = devices.find((d) => d.deviceId === deviceId);
    return (device && device.label) || "Unknown Device";
  };

  openMenu = (kind, event) => {
    if (kind === "camera" && this.props.isAudioOnlyConsult) return;
    this.setState({
      openKind: kind,
      anchorEl: event.currentTarget,
    });
    // If device list is empty (e.g. after permissions blocked), refresh.
    if (
      (kind === "microphone" && this.state.audioInputs.length === 0) ||
      (kind === "speaker" && this.state.audioOutputs.length === 0) ||
      (kind === "camera" && this.state.videoInputs.length === 0)
    ) {
      this.loadDevices();
    }
  };

  closeMenu = (event, reason) => {
    // Only close the popup when clicked outside of the menu.
    if (reason !== "backdropClick") return;

    this.stopMicRecording();
    this.stopMicPlayback();
    this.setState({ anchorEl: null, openKind: null });
  };

  handleSelect = async (kind, deviceId) => {
    if (!deviceId) return;

    const storageKey = STORAGE_KEYS[kind];
    localStorage.setItem(storageKey, deviceId);

    this.setState({
      selectedMicInput:
        kind === "microphone" ? deviceId : this.state.selectedMicInput,
      selectedCallingDevice:
        kind === "speaker" ? deviceId : this.state.selectedCallingDevice,
      selectedCamera:
        kind === "camera" ? deviceId : this.state.selectedCamera,
    });

    // Live call: switch the active Twilio media devices immediately.
    if (this.props.isLiveCall && this.props.onDeviceSwitch) {
      try {
        await this.props.onDeviceSwitch(kind, deviceId);
      } catch (err) {
        console.error("ConsultDeviceDock: onDeviceSwitch failed", err);
        toast.error("Failed to switch device. Please try again.");
      }
    }

    // If switching devices while testing, stop any active mic recording/playback.
    this.stopMicRecording();
    this.stopMicPlayback();
  };

  stopMicRecording = () => {
    // Prevent `MediaRecorder.onstop` from committing results after cancellation.
    this._ignoreNextRecordingResult = true;

    const { mediaRecorder, audioStream } = this.state;
    try {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
    } catch (e) {
      // ignore
    }
    try {
      if (audioStream) {
        audioStream.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {
      // ignore
    }

    if (this.state.isRecording) {
      this.setState({
        isRecording: false,
        mediaRecorder: null,
        audioStream: null,
      });
    }
  };

  stopMicPlayback = () => {
    const { playbackAudio } = this.state;
    if (playbackAudio) {
      try {
        playbackAudio.pause();
        playbackAudio.currentTime = 0;
      } catch (e) {
        // ignore
      }
    }
    this.setState({ isPlayingRecording: false, playbackAudio: null });
  };

  handleStartMicRecording = async () => {
    const { selectedMicInput } = this.state;

    if (!selectedMicInput) {
      toast.warning("Please select a microphone first");
      return;
    }

    try {
      // Discard any previous recording and avoid object URL leaks.
      if (this.state.recordedAudioUrl) {
        try {
          URL.revokeObjectURL(this.state.recordedAudioUrl);
        } catch (e) {
          // ignore
        }
      }

      this._ignoreNextRecordingResult = false;
      // stop any previous playback before recording
      this.stopMicPlayback();

      const constraints = {
        audio: {
          deviceId: { exact: selectedMicInput },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        try {
          if (stream) {
            stream.getTracks().forEach((t) => t.stop());
          }
        } catch (e) {
          // ignore
        }

        if (this._isUnmounted || this._ignoreNextRecordingResult) {
          // Cancelled/closed/unmounted: don't update the UI with a new blob.
          this._ignoreNextRecordingResult = false;
          if (!this._isUnmounted) {
            this.setState({
              recordedAudioBlob: null,
              recordedAudioUrl: null,
              isRecording: false,
              mediaRecorder: null,
              audioStream: null,
            });
          }
          return;
        }

        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);

        // If recording completed normally, clear the cancel flag.
        this._ignoreNextRecordingResult = false;

        this.setState({
          recordedAudioBlob: blob,
          recordedAudioUrl: url,
          isRecording: false,
          mediaRecorder: null,
          audioStream: null,
        });
      };

      this.setState({
        isRecording: true,
        mediaRecorder,
        audioStream: stream,
      });
      toast.info("Recording started... Speak into your microphone");
      mediaRecorder.start();
    } catch (err) {
      console.error("ConsultDeviceDock: start mic recording failed", err);
      toast.error("Failed to start recording. Please check microphone permissions.");
      this.setState({ isRecording: false, mediaRecorder: null, audioStream: null });
    }
  };

  handleStopMicRecording = () => {
    const { mediaRecorder } = this.state;
    try {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      } else {
        this.stopMicRecording();
      }
    } catch (e) {
      // ignore
    }
  };

  handlePlayRecordedMicAudio = async () => {
    const { recordedAudioUrl, selectedCallingDevice } = this.state;
    if (!recordedAudioUrl) {
      toast.warning("No recording available. Please record first.");
      return;
    }
    if (!selectedCallingDevice) {
      toast.warning("Please select a headset/speaker device first");
      return;
    }

    try {
      // Stop existing playback first
      this.stopMicPlayback();

      const audio = new Audio(recordedAudioUrl);
      if (typeof audio.setSinkId === "function") {
        await audio.setSinkId(selectedCallingDevice);
      }

      audio.onended = () => {
        this.setState({ isPlayingRecording: false, playbackAudio: null });
      };
      audio.onerror = () => {
        this.setState({ isPlayingRecording: false, playbackAudio: null });
        toast.error("Failed to play recorded audio");
      };

      await audio.play();
      this.setState({ isPlayingRecording: true, playbackAudio: audio });
    } catch (err) {
      console.error("ConsultDeviceDock: play recorded mic audio failed", err);
      toast.error("Failed to play recorded audio");
      this.setState({ isPlayingRecording: false, playbackAudio: null });
    }
  };

  handleToggleHeadsetTest = async () => {
    const { selectedCallingDevice, showHeadsetTestButtonIcon } = this.state;
    if (!selectedCallingDevice) {
      toast.warning("Please select a headset/speaker first");
      return;
    }
    if (showHeadsetTestButtonIcon) {
      // currently shows play icon -> clicking starts test, then toggles to pause icon
      this.setState({ showHeadsetTestButtonIcon: false });
      await playNotificationSound(false, selectedCallingDevice);
    } else {
      this.setState({ showHeadsetTestButtonIcon: true });
      await playNotificationSound(true, selectedCallingDevice);
    }
  };

  renderMenu() {
    const { anchorEl, openKind } = this.state;
    if (!openKind) return null;

    const { audioInputs, audioOutputs, videoInputs } = this.state;
    let devices = [];
    let selectedId = "";

    if (openKind === "microphone") {
      devices = audioInputs;
      selectedId = this.state.selectedMicInput;
    } else if (openKind === "speaker") {
      devices = audioOutputs;
      selectedId = this.state.selectedCallingDevice;
    } else if (openKind === "camera") {
      devices = videoInputs;
      selectedId = this.state.selectedCamera;
    }

    const disabled = this.props.isAudioOnlyConsult && openKind === "camera";

    return (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={this.closeMenu}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: "60vh",
            overflowY: "auto",
            borderRadius: "14px",
            overflow: "hidden",
            backgroundColor: "#fff",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.25)",
            marginTop: "6px",
          },
        }}
      >
        <Box
          px={2}
          py={1.25}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            backgroundColor: "#f1f3f4",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            userSelect: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {openKind === "microphone" ? (
              <MicIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.65)" }} />
            ) : null}
            {openKind === "speaker" ? (
              <HeadphonesIcon
                fontSize="small"
                sx={{ color: "rgba(0,0,0,0.65)" }}
              />
            ) : null}
            {openKind === "camera" ? (
              <VideocamIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.65)" }} />
            ) : null}

            <Typography
              variant="subtitle2"
              sx={{ color: "#202124", fontWeight: 600 }}
            >
              {openKind === "speaker"
                ? "Select a Headset"
                : openKind === "microphone"
                  ? "Select a Microphone"
                  : "Select a Camera"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {openKind === "speaker" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleToggleHeadsetTest();
                }}
                sx={{
                  backgroundColor: "#1a73e8",
                  color: "#fff",
                  width: 26,
                  height: 26,
                  p: 0,
                  borderRadius: "7px",
                  "&:hover": { backgroundColor: "#1665c1" },
                }}
              >
                {this.state.showHeadsetTestButtonIcon ? (
                  <PlayArrowIcon fontSize="8px" />
                ) : (
                  <PauseIcon fontSize="8px" />
                )}
              </IconButton>
            ) : null}

            {openKind === "microphone" ? (
              this.state.isRecording ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.handleStopMicRecording();
                  }}
                  sx={{
                    backgroundColor: "#e53935",
                    color: "#fff",
                    width: 26,
                    height: 26,
                    p: 0,
                    borderRadius: "7px",
                    "&:hover": { backgroundColor: "#d32f2f" },
                  }}
                >
                  <StopIcon fontSize="8px" />
                </IconButton>
              ) : this.state.recordedAudioUrl ? (
                <React.Fragment>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.handleStartMicRecording();
                    }}
                    sx={{
                      backgroundColor: "#e53935",
                      color: "#fff",
                      width: 26,
                      height: 26,
                      p: 0,
                      borderRadius: "7px",
                      "&:hover": { backgroundColor: "#d32f2f" },
                    }}
                  >
                    <MicIcon fontSize="8px" />
                  </IconButton>

                  {this.state.isPlayingRecording ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.stopMicPlayback();
                      }}
                      sx={{
                        backgroundColor: "#2e7d32",
                        color: "#fff",
                        width: 26,
                        height: 26,
                        p: 0,
                        borderRadius: "7px",
                        "&:hover": { backgroundColor: "#256d2b" },
                      }}
                    >
                      <StopIcon fontSize="8px" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handlePlayRecordedMicAudio();
                      }}
                      sx={{
                        backgroundColor: "#2e7d32",
                        color: "#fff",
                        width: 26,
                        height: 26,
                        p: 0,
                        borderRadius: "7px",
                        "&:hover": { backgroundColor: "#256d2b" },
                      }}
                    >
                      <PlayArrowIcon fontSize="8px" />
                    </IconButton>
                  )}
                </React.Fragment>
              ) : (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.handleStartMicRecording();
                  }}
                  sx={{
                    backgroundColor: "#e53935",
                    color: "#fff",
                    width: 26,
                    height: 26,
                    p: 0,
                    borderRadius: "7px",
                    "&:hover": { backgroundColor: "#d32f2f" },
                  }}
                >
                  <MicIcon fontSize="8px" />
                </IconButton>
              )
            ) : null}
          </Box>
        </Box>

        {disabled ? (
          <MenuItem disabled>Camera disabled for Audio-only consult</MenuItem>
        ) : devices.length ? (
          devices.map((d) => (
            <MenuItem
              key={`${openKind}-${d.deviceId}`}
              onClick={() => this.handleSelect(openKind, d.deviceId)}
              sx={{
                py: "8px",
                px: 2,
                alignItems: "flex-start",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                <Radio size="small" checked={d.deviceId === selectedId} />

                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    flex: 1,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "#202124",
                    marginTop: "1px",
                  }}
                >
                  {d.label || "Unknown Device"}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            No devices found (check permissions / connect devices)
          </MenuItem>
        )}
      </Menu>
    );
  }

  render() {
    const { loadingDevices, audioInputs, audioOutputs, videoInputs } =
      this.state;

    const sharedPillSx = {
      backgroundColor: "rgba(0,0,0,0.40)",
      border: "1px solid rgba(255,255,255,0.16)",
      borderRadius: "18px",
      padding: "6px 10px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      minHeight: 34,
      cursor: "pointer",
      userSelect: "none",
      color: "#fff",
      backdropFilter: "blur(6px)",
    };

    const iconSx = { color: "#fff" };
    const expandSx = { color: "rgba(255,255,255,0.65)" };

    const cameraDisabled = this.props.isAudioOnlyConsult;
    const isExpandedKind = (kind) => this.state.openKind === kind;

    if (this.getVariant() === "icons") {
      return (
        <React.Fragment>
          <Box display="flex" alignItems="center" justifyContent="center" gap="10px">
            <IconButton
              onClick={(e) => this.openMenu("microphone", e)}
              disabled={loadingDevices || audioInputs.length === 0}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.22)",
                color: "#fff",
                opacity: loadingDevices || audioInputs.length === 0 ? 0.5 : 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
              }}
              title="Microphone"
            >
              <MicIcon fontSize="small" />
            </IconButton>

            <IconButton
              onClick={(e) => this.openMenu("speaker", e)}
              disabled={loadingDevices || audioOutputs.length === 0}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.22)",
                color: "#fff",
                opacity: loadingDevices || audioOutputs.length === 0 ? 0.5 : 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
              }}
              title="Headset / Speaker"
            >
              <HeadphonesIcon fontSize="small" />
            </IconButton>

            <IconButton
              onClick={(e) => this.openMenu("camera", e)}
              disabled={loadingDevices || cameraDisabled || videoInputs.length === 0}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.22)",
                color: "#fff",
                opacity:
                  loadingDevices || cameraDisabled || videoInputs.length === 0 ? 0.5 : 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
              }}
              title={cameraDisabled ? "Camera disabled (Audio-only consult)" : "Camera"}
            >
              <VideocamIcon fontSize="small" />
            </IconButton>
          </Box>

          {this.renderMenu()}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Box display="flex" alignItems="center" justifyContent="center" gap="10px">
          <Box
            component="div"
            sx={{
              ...sharedPillSx,
            }}
            onClick={(e) => this.openMenu("microphone", e)}
            aria-disabled={loadingDevices}
          >
            <MicIcon fontSize="small" sx={iconSx} />
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                ...expandSx,
                transform: isExpandedKind("microphone") ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 120ms ease-in-out",
              }}
            />
          </Box>

          <Box
            component="div"
            sx={{
              ...sharedPillSx,
            }}
            onClick={(e) => this.openMenu("speaker", e)}
            aria-disabled={loadingDevices}
          >
            <HeadphonesIcon fontSize="small" sx={iconSx} />
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                ...expandSx,
                transform: isExpandedKind("speaker") ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 120ms ease-in-out",
              }}
            />
          </Box>

          <Box
            component="div"
            sx={{
              ...sharedPillSx,
              opacity: cameraDisabled ? 0.45 : 1,
              pointerEvents: cameraDisabled ? "none" : "auto",
            }}
            onClick={(e) => this.openMenu("camera", e)}
          >
            <VideocamIcon fontSize="small" sx={iconSx} />
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                ...expandSx,
                transform: isExpandedKind("camera") ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 120ms ease-in-out",
              }}
            />
          </Box>
        </Box>

        {this.renderMenu()}
      </React.Fragment>
    );
  }
}

export default ConsultDeviceDock;




// WEBPACK FOOTER //
// ./src/components/common/ConsultDeviceDock.js